import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlayIcon, XIcon } from 'lucide-react';
import { SectionHeading } from '../components/SectionHeading';
import { SocialStream } from '../components/SocialStream';
import { useLanguage } from '../contexts/LanguageContext';
import { photos, filterKeys, type FilterKey, type Photo } from '../data/media';
import { mediaText } from '../data/mediaText';
import { ui } from '../data/ui';
export function Portfolio() {
  const {
    language
  } = useLanguage();
  const strings = ui[language];
  const text = mediaText[language];
  const [filter, setFilter] = useState<FilterKey>('all');
  const [lightbox, setLightbox] = useState<Photo | null>(null);
  const visiblePhotos = useMemo(() => filter === 'all' ? photos : photos.filter(photo => photo.category === filter), [filter]);
  return <div className="w-full bg-blush-100" data-id="element-146" data-mp="156nzu8nszgzd">
      <section className="mx-auto max-w-6xl px-5 pb-10 pt-12 sm:px-8 sm:pt-16" data-id="element-147" data-mp="157pmlae6lrwx">
        <p className="text-xs uppercase tracking-[0.3em] text-ink/50" data-id="element-148" data-mp="16alvwh0s9mlh">
          {strings.portfolio.eyebrow}
        </p>
        <h1 className="mt-3 font-script text-5xl text-ink sm:text-6xl" data-id="element-149" data-mp="1b9ykp7b7t3qp">
          {strings.portfolio.title}
        </h1>
        <div className="mt-5 h-1.5 w-full bg-blush-300" data-id="element-150" data-mp="1g6yplewbw0g8" />
        <p className="mt-5 max-w-2xl text-sm leading-relaxed text-ink/75 sm:text-base" data-id="element-151" data-mp="1kz9pihr2jm8s">
          {strings.portfolio.intro}
        </p>
      </section>

      {/* Photos */}
      <section className="mx-auto max-w-6xl px-5 pb-16 sm:px-8" data-id="element-152" data-mp="1tzocrmnlfbex">
        <SectionHeading data-id="element-153" data-mp="1txv647czcr6x">{strings.sections.photos}</SectionHeading>

        <div className="mt-6 flex flex-wrap gap-2" role="tablist" aria-label={strings.portfolio.filterLabel} data-id="element-154" data-mp="1yzswdljssm9d">
          {filterKeys.map(key => {
          const active = key === filter;
          return <button key={key} type="button" role="tab" aria-selected={active} onClick={() => setFilter(key)} className={`rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] transition-colors ${active ? 'bg-ink text-blush-50' : 'border border-ink/20 text-ink/60 hover:bg-white hover:text-ink'}`} data-id="element-155" data-mp="4n32h3tfjd1z">
                {text.categories[key]}
              </button>;
        })}
        </div>

        <motion.ul layout className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-156" data-mp="gge7ika36ak2">
          <AnimatePresence mode="popLayout" data-id="element-157" data-mp="gebgbwx76bdr">
            {visiblePhotos.map(photo => {
            const meta = text.photos[photo.id];
            return <motion.li key={photo.id} layout initial={{
              opacity: 0,
              y: 12
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -8
            }} transition={{
              duration: 0.35,
              ease: 'easeOut'
            }} data-id="element-158" data-mp="pa0ylx3cmpmx">
                  <button type="button" onClick={() => setLightbox(photo)} className="group block w-full overflow-hidden bg-white text-left" data-id="element-159" data-mp="1itgxi879yeix">
                    <span className="block overflow-hidden" data-id="element-160" data-mp="18wvqjhbp2tcg">
                      <img src={photo.src} alt={meta.alt} loading="lazy" className="aspect-[2/3] w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" data-id="element-161" data-mp="1ds1d8icx8c1l" />
                    </span>
                    <span className="flex items-baseline justify-between gap-3 px-4 py-3" data-id="element-162" data-mp="q1xbkgjammd8">
                      <span className="text-sm font-medium text-ink" data-id="element-163" data-mp="v3cvg59qimu1">{meta.caption}</span>
                      <span className="text-[10px] uppercase tracking-[0.2em] text-ink/45" data-id="element-164" data-mp="g6dpeluj721o">
                        {text.categories[photo.category]}
                      </span>
                    </span>
                  </button>
                </motion.li>;
          })}
          </AnimatePresence>
        </motion.ul>
      </section>

      {/* Reels */}
      <section className="bg-blush-200/60 py-16" data-id="element-165" data-mp="1ep9k4mw58r1l">
        <div className="mx-auto max-w-6xl px-5 sm:px-8" data-id="element-166" data-mp="1enyjux1wq1f8">
          <SectionHeading variant="plain" data-id="element-167" data-mp="19pwvpxpmjbww">{strings.sections.reels}</SectionHeading>
          <p className="mt-4 max-w-2xl text-sm text-ink/70" data-id="element-168" data-mp="14s3tffrfz21w">{strings.portfolio.reelsIntro}</p>
          <div className="mt-8 flex min-h-[340px] flex-col items-center justify-center gap-6 bg-blush-100 px-6 py-14" data-id="element-169" data-mp="10pqouuofla6p">
            <img src="https://cdn.magicpatterns.com/uploads/3F4XeLdDYSjonep86TPRpo/image.png" alt="" aria-hidden="true" className="w-full max-w-md" data-id="element-170" data-mp="vs8gp6i8myzr" />
            <p className="flex items-center gap-3 text-sm font-semibold uppercase tracking-[0.3em] text-ink/70" data-id="element-171" data-mp="2q4oi9u039oya">
              <PlayIcon className="h-5 w-5" aria-hidden="true" data-id="element-172" data-mp="2l40qh641d430" />
              Coming soon
            </p>
          </div>
        </div>
      </section>

      {/* Snippets */}
      <section className="mx-auto max-w-6xl px-5 py-16 sm:px-8" style={{
      backgroundColor: "#EED9D7"
    }} data-id="element-173" data-mp="2xneh4rl9y1jc">
        <SectionHeading data-id="element-174" data-mp="32fxxfwbcjaru">{strings.sections.snippets}</SectionHeading>
        <div className="mt-8 flex min-h-[340px] flex-col items-center justify-center gap-6 bg-blush-200/60 px-6 py-14" style={{
        backgroundColor: "#F6E7E6"
      }} data-id="element-175" data-mp="37esdiqhxil89">
          <img src="https://cdn.magicpatterns.com/uploads/3F4XeLdDYSjonep86TPRpo/image.png" alt="" aria-hidden="true" className="w-full max-w-md" style={{
          height: "211px"
        }} data-id="element-176" data-mp="29t4pasvmn97t" />
          <p className="flex items-center gap-3 text-sm font-semibold uppercase tracking-[0.3em] text-ink/70" data-id="element-177" data-mp="2jr13jtjv2ka5">
            <PlayIcon className="h-5 w-5" aria-hidden="true" data-id="element-178" data-mp="2olojh40su6d4" />
            Coming soon
          </p>
        </div>
      </section>

      {/* Social */}
      <SocialStream data-id="element-179" data-mp="3wqvxpmntw4w" />

      {/* Lightbox */}
      <AnimatePresence data-id="element-180" data-mp="3zmyjr9gd8zo">
        {lightbox && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center bg-ink/85 p-4" role="dialog" aria-modal="true" aria-label={text.photos[lightbox.id].caption} onClick={() => setLightbox(null)} data-id="element-181" data-mp="1zzvrnhgsyykq">
            <button type="button" onClick={() => setLightbox(null)} aria-label={strings.portfolio.closeLabel} className="absolute right-5 top-5 rounded-full bg-blush-50/90 p-2 text-ink" data-id="element-182" data-mp="1hbidcorhwkdw">
              <XIcon className="h-5 w-5" data-id="element-183" data-mp="1q4lkkfbjs103" />
            </button>
            <motion.figure initial={{
          scale: 0.96,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.96,
          opacity: 0
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} className="max-h-full w-full max-w-3xl" onClick={event => event.stopPropagation()} data-id="element-184" data-mp="1lahgc3to272p">
              <img src={lightbox.src} alt={text.photos[lightbox.id].alt} className="max-h-[75vh] w-full object-contain" data-id="element-185" data-mp="o7wdawh44hqk" />
              <figcaption className="mt-3 text-center text-sm uppercase tracking-[0.2em] text-blush-50" data-id="element-186" data-mp="ehl876lp02i3">
                {text.photos[lightbox.id].caption}
              </figcaption>
            </motion.figure>
          </motion.div>}
      </AnimatePresence>
    </div>;
}